/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global gettext, process */
import React from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Field, reduxForm, formValueSelector } from 'redux-form';

import { isPending } from 'consts/requestStates';
import { email, strongPassword } from 'validators';
import { getAllCollection, USER_ROLES } from 'actions/collections';
import FormError from 'components/form/FormError';
import SubmitButton from 'components/form/SubmitButton';
import FieldWithError from 'components/form/FieldWithError';
import RoleOptions from 'components/tenants/RoleOptions';


const renderRoleOption = field => (
  field.active ? (
    <RoleOptions
      name={field.input.name}
      {...field}
    />
  ) : (
    <input type="text" value={field.input.value} name="role" disabled />
  )
);

export class EditCreateUserForm extends React.PureComponent {
  componentWillMount() {
    this.props.loadUserRoles();
  }

  render() {
    const {
      requestState,
      handleSubmit,
      onCancel,
      invalid,
      offices,
      editing,
      canSetOffice,
      disabled,
      selectedRoleId,
      roleOptions,
      userCanUpdateRole,
    } = this.props;

    const pending = isPending(requestState);

    const selectedRole = roleOptions && roleOptions.find(role => role.get('id') === selectedRoleId);
    const selectedRoleRequiresOffice = selectedRole && selectedRole.get('office_scope');

    const roleOptionsToShow = canSetOffice
      ? roleOptions
      : roleOptions && roleOptions.filter(role => role.get('office_scope'));

    return (
      <div>
        <form className="tenants-create-tenant-form" onSubmit={handleSubmit}>
          <span className="list-search-container pull-right">
            <label htmlFor="role">{gettext('Role')}</label>
            <FormError name="role" requestState={requestState} />
            <Field
              name="role"
              component={renderRoleOption}
              active={userCanUpdateRole}
              roleOptions={roleOptionsToShow}
              disabled={disabled}
            />

            {canSetOffice && offices && selectedRoleRequiresOffice && (
              <div>
                <label htmlFor="office">{gettext('Affiliated Office')}</label>
                <FormError name="office" requestState={requestState} />
                <Field
                  component="select"
                  name="office"
                  disabled={offices.isEmpty() || disabled}
                >
                  <option value="" disabled>{gettext('Please select an office')}</option>
                  {offices
                    .sortBy(office => office.get('name'))
                    .map(office => (
                      <option key={office.get('id')} value={office.get('id')}>
                        {office.get('name')}
                      </option>
                    ))
                  }
                </Field>
              </div>
            )}

            <label htmlFor="first_name">{gettext('First Name')}</label>
            <FormError name="first_name" requestState={requestState} />
            <Field
              component="input"
              type="text"
              name="first_name"
              placeholder={gettext('Administrator First Name')}
              disabled={disabled}
            />

            <label htmlFor="last_name">{gettext('Last Name')}</label>
            <FormError name="last_name" requestState={requestState} />
            <Field
              component="input"
              type="text"
              name="last_name"
              placeholder={gettext('Administrator Last Name')}
              disabled={disabled}
            />

            <label htmlFor="email">{gettext('Email/Username')}</label>
            <FormError name="email" requestState={requestState} />
            <Field
              component={FieldWithError}
              innerComponent="input"
              type="text"
              name="email"
              placeholder={gettext('Email Address')}
              disabled={disabled}
              validate={email}
            />

            {editing ? (
              <div>
                <div className="is-active">
                  <label htmlFor="is_active">
                    <Field
                      name="is_active"
                      component="input"
                      type="radio"
                      value="true"
                      disabled={disabled}
                    />
                    {gettext('Active')}
                  </label>
                  <label htmlFor="is_active">
                    <Field
                      name="is_active"
                      component="input"
                      type="radio"
                      value="false"
                      disabled={disabled}
                    />
                    {gettext('Suspended')}
                  </label>
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="password">{gettext('Password')}</label>
                <FormError name="password" requestState={requestState} />
                <Field
                  component={FieldWithError}
                  innerComponent="input"
                  type="password"
                  name="password"
                  placeholder={gettext('Administrator Password')}
                  props={{ autoComplete: 'new-password', disabled }}
                  validate={strongPassword}
                />
              </div>
            )}

            <FormError name="non_field_errors" requestState={requestState} />

            <button className="button secondary" onClick={onCancel} disabled={pending}>
              {gettext('Cancel')}
            </button>

            <SubmitButton
              disabled={disabled}
              valid={!invalid}
              onClick={handleSubmit}
              pending={pending}
              pendingText={editing ? gettext('Updating...') : gettext('Creating...')}
            >
              {editing ? gettext('Update') : gettext('Create')}
            </SubmitButton>

          </span>
        </form>
      </div>
    );
  }
}

EditCreateUserForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  requestState: ImmutablePropTypes.map.isRequired,
  offices: ImmutablePropTypes.list,
  invalid: PropTypes.bool.isRequired,
  editing: PropTypes.bool,
  disabled: PropTypes.bool,
  canSetOffice: PropTypes.bool.isRequired,
  roleOptions: ImmutablePropTypes.list,
  loadUserRoles: PropTypes.func.isRequired,
  selectedRoleId: PropTypes.string,
  userCanUpdateRole: PropTypes.bool.isRequired,
};
EditCreateUserForm.defaultProps = {
  editing: false,
  disabled: false,
  offices: null,
  roleOptions: null,
  selectedRoleId: null,
};

const formName = 'createUser';

const CreateUserReduxForm = reduxForm({
  form: formName,
})(EditCreateUserForm);

const selector = formValueSelector(formName);

export function mapStateToProps(state) {
  return {
    selectedRoleId: selector(state, 'role'),
    roleOptions: state.collections.getIn([USER_ROLES, 'items']),
  };
}

export function mapDispatchToProps(dispatch) {
  return {
    loadUserRoles: () => dispatch(getAllCollection(USER_ROLES)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateUserReduxForm);
